/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import { DeviceModelDto } from 'api/deviceModels/models/DeviceModelDto';
import UsersService from 'api/users/UsersService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { Button, Card, Container, Dropdown, Form, Stack } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Reducers } from 'store/types';
import styles from './DeviceModelsScreen.module.scss';
import DeviceModelsService from 'api/deviceModels/DeviceModelsService';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import { Paged } from 'api/common/types/Page';
import { DeviceModelSearchCriteria } from 'api/deviceModels/models/DeviceModelSearchCriteria';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import DeviceBrandsService from 'api/deviceBrands/DeviceBrandsService';
import { SelectValueLabel } from 'common/types/SelectValueLabel';

type Props = {
};

const DeviceModelsScreen: React.FC<Props> = () => {
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasDeviceModelsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['CONFIG_DEVICE_BRAND_WRITE']);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [brands, setBrands] = useState<SelectValueLabel[]>();
    const [criteria, setCriteria] = useState<DeviceModelSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderBy: 'asc',
        orderColumn: 'dm.name'
    });
    const [filters, setFilters] = useState({ name: '', deviceBrandId: '' });
    const [deviceModelsPage, setDeviceModelsPage] = useState<Paged<DeviceModelDto>>();
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<DeviceModelDto | null>(null);

    const showRemoveItemDialog = async (item: DeviceModelDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }
        try {
            await DeviceModelsService.remove(itemToRemove);
            onCancelRemove();
            toast.success(t('messages.record_delete_success'));
            setCriteria({ ...criteria, page: 1 });
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete device models', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    const renderTableActionCell = (row: DeviceModelDto) => {
        return <Dropdown>
            <Dropdown.Toggle id="dropdown-basic-button" size="sm" variant="outline-secondary">...</Dropdown.Toggle>
            <Dropdown.Menu popperConfig={{ strategy: 'fixed' }} renderOnMount>
                <Dropdown.Item onClick={() => navigate(`/devicemodel/details/${row.id}`, { state: { row } })}>{t('common.details')}</Dropdown.Item>
                {hasDeviceModelsWritePolicy && <>
                    <Dropdown.Item onClick={() => navigate(`/devicemodel/edit/${row.id}`, { state: { row } })}>{t('common.edit')}</Dropdown.Item>
                    <Dropdown.Item onClick={() => showRemoveItemDialog(row)}>{t('common.delete')}</Dropdown.Item>
                </>
                }
            </Dropdown.Menu>
        </Dropdown>
    };

    useEffect(() => {
        DeviceBrandsService.getDeviceBrands()
            .then((data) => { setBrands(data); })
            .catch(console.error);
    }, []);

    useEffect(() => {
        search().catch(console.error)
    }, [criteria]);

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const onRowClick = (event: any, row?: DeviceModelDto) => {
        if (row?.id) {
            if (event.ctrlKey) {
                window.open('/devicemodel/details/' + row.id, '_blank');
                return;
            }
            navigate(`/devicemodel/details/${row.id}`, { state: { row } });
        }
    }

    function handleFilterChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFilters({ ...filters, [key]: value });
    }

    const onSearch = () => {
        setCriteria({ ...criteria, page: 1, name: filters.name, deviceBrandId: filters.deviceBrandId });
    }

    const clearFilters = () => {
        setFilters({ name: '', deviceBrandId: '' });
        setCriteria({ ...criteria, page: 1, name: '', deviceBrandId: '' });
    }

    const tableColumns: ListingTableColumn<DeviceModelDto>[] = [
        {
            field: 'name',
            name: t('config_devices.models.list.name') ?? '',
            onSearch: onTableFilter,
            searchField: 'dm.name'
        },
        {
            field: 'deviceBrandName',
            name: t('config_devices.models.list.brand') ?? '',
            onSearch: onTableFilter,
            searchField: 'db.name'
        },
        {
            name: t('config_devices.models.list.has_can_reading') ?? '',
            width: '120px',
            cellAlignment: 'center',
            renderCell: (row: DeviceModelDto) => <span>{row.hasCanReading ? '\u2713' : ''}</span>
        },
        {
            name: t('config_devices.models.list.supports_identification_tag') ?? '',
            width: '180px',
            cellAlignment: 'center',
            renderCell: (row: DeviceModelDto) => <span>{row.supportsIdentificationTag ? '\u2713' : ''}</span>
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' }
        },
    ];

    const search = async () => {
        try {
            Loading.show();
            const page = await DeviceModelsService.getList(criteria);
            setDeviceModelsPage(page);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get device model list', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    return (<>
        <Container fluid>
            <Card className={styles.filters}>
                <Card.Body>
                    <Stack direction="horizontal" gap={3}>
                        <Form.Select name="deviceBrandId" value={filters.deviceBrandId} onChange={handleFilterChange}>
                            <option value={''}>{t('common.select_option')}</option>
                            {brands && brands.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                        </Form.Select>
                        <Form.Control type="text" value={filters.name} name="name" onChange={handleFilterChange} onKeyDown={(e: any) => { if (e.key === 'Enter') onSearch(); }} placeholder={t('config_devices.models.list.name')} />
                        <Button variant="secondary" onClick={onSearch}>{t('common.search')}</Button>
                        <div className="vr" />
                        <Button variant="outline-danger" onClick={clearFilters}>{t('common.clear')}</Button>
                        {hasDeviceModelsWritePolicy && <Button variant="primary" onClick={() => navigate('/devicemodel/new/')}>{t('common.new')}</Button>}
                    </Stack>
                </Card.Body>
            </Card>
        </Container>
        <Container fluid>
            <ListingTable
                columns={tableColumns}
                rows={deviceModelsPage?.items || []}
                onRowClick={(row, _, event) => onRowClick(event, row)}
                allowHover={true}
                initialSearch={{ colField: 'dm.name', isOrderAsc: true }}
            />
            <PaginationWithInfo
                itemName={t('config_devices.models.title')}
                currentPage={deviceModelsPage?.currentPage ?? 1}
                pageItems={deviceModelsPage?.items.length || 0}
                totalItems={deviceModelsPage?.totalItems || 0}
                onChange={page => setCriteria({ ...criteria, page })} />
        </Container>
        <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: itemToRemove?.name ?? '' })} />
    </>);
};

export default DeviceModelsScreen;
